import { CountryGridEvent } from 'editor/src/store/gridConfig/types';

export interface CountryHoliday {
  date: string;
  title: string;
  countries: string[];
}

// Order matters: it orders the holidays of a date several countries share.
export const isSameCountries = (a: string[] | undefined, b: string[] | undefined) =>
  !!a && !!b && a.length === b.length && a.every((country, index) => country === b[index]);

// One holiday per name and date, so a date countries call differently shows each name; the user unticks the extras.
export const mergeCountryHolidays = (countries: string[], events: CountryGridEvent[]): CountryHoliday[] => {
  const byDateAndTitle = new Map<string, CountryHoliday>();
  countries.forEach((country) => {
    events
      .filter((event) => event.country === country)
      .forEach(({ date, title }) => {
        const key = `${date}\n${title}`;
        const holiday = byDateAndTitle.get(key);
        if (!holiday) {
          byDateAndTitle.set(key, { date, title, countries: [country] });
        } else if (!holiday.countries.includes(country)) {
          holiday.countries.push(country);
        }
      });
  });
  return [...byDateAndTitle.values()].sort((a, b) => a.date.localeCompare(b.date));
};

export const getCountryNames = (countries: string[], language: string) => {
  let regionNames: Intl.DisplayNames | undefined;
  try {
    regionNames = new Intl.DisplayNames([language], { type: 'region' });
  } catch {
    regionNames = undefined;
  }
  return countries.map((country) => regionNames?.of(country) ?? country);
};

export const getCountryOptions = (countries: string[], language: string) => {
  const names = getCountryNames(countries, language);
  return countries
    .map((country, index) => ({ value: country, label: names[index] }))
    .sort((a, b) => a.label.localeCompare(b.label, language));
};
